import { FC, ReactNode } from 'react';
import { Box, Grid, makeStyles, Paper, Theme, Typography } from '@material-ui/core';
import { useMediaUp } from 'hooks';
// @ts-ignore
import ModulesBackground from '../../../svgs/gpm/modulesBackground.svg?url';
// @ts-ignore
import BackgroundMobile from '../../../svgs/gpm/backgroundMobile.svg?url';

interface ModuleBlockProps {
  title: string;
  description?: string;
  listItems: Array<{
    title: string;
    icon: ReactNode;
    text: string;
  }>;
}

const useStyles = makeStyles((theme: Theme) => ({
  root: {
    display: 'flex',
    padding: theme.spacing(10, 3, 10),
    justifyContent: 'center',
    backgroundImage: `url(${BackgroundMobile})`,
    backgroundRepeat: 'no-repeat',
    backgroundSize: 'cover',
    marginBottom: theme.spacing(5),
    backgroundPosition: 'center',
    marginTop: 0,
    minHeight: 959,
    [theme.breakpoints.up('sm')]: {
      paddingLeft: theme.spacing(4),
      backgroundImage: `url(${ModulesBackground})`,
    },
    [theme.breakpoints.up('md')]: {
      marginBottom: theme.spacing(15),
      padding: theme.spacing(17, 3),
    },
    [theme.breakpoints.up('lg')]: {
      padding: theme.spacing(17, 39),
    },
    [theme.breakpoints.up('xl')]: {
      padding: theme.spacing(17, 74),
    },
  },
  title: {
    lineHeight: 1.3,
    marginBottom: theme.spacing(3),
    fontWeight: 700,
    color: theme.palette.common.white,
    fontSize: 50,
    textAlign: 'center',
    fontFamily: 'Helvetica Neue, sans-serif',
    [theme.breakpoints.up('sm')]: {
      textAlign: 'left',
      marginBottom: theme.spacing(5),
    },
  },
  description: {
    color: theme.palette.common.white,
    fontSize: 15,
    lineHeight: '20px',
    fontFamily: 'Helvetica Neue, sans-serif',
    '& a': {
      textDecoration: 'underline',
    },
  },
  link: {
    boxShadow: 'none',
    borderRadius: '0 10px',
    padding: '14px 35px',
    textTransform: 'none',
    fontSize: 15,
    lineHeight: 1,
    fontWeight: 500,
    color: theme.palette.common.white,
    border: '2px solid #fff',
    fontFamily: 'Helvetica Neue, sans-serif',
    backgroundColor: 'transparent',
    transition: 'all 0.3s ease',
    '&:hover': {
      backgroundColor: '#ffffff',
      color: '#0061E5',
    },
  },
  card: {
    backgroundColor: theme.palette.common.white,
    borderRadius: 12,
    width: 146,
    minHeight: 160,
    padding: '10px 5px',
    [theme.breakpoints.up('sm')]: {
      width: 297,
      padding: '15px 10px',
      minHeight: 114,
      maxHeight: 114,
    },
  },
  cardTop: {
    backgroundColor: theme.palette.common.white,
    borderRadius: 12,
    width: 199,
    minHeight: 136,
    padding: '10px 5px',
    [theme.breakpoints.up('sm')]: {
      width: 297,
      padding: 10,
      minHeight: 119,
    },
  },
  cardTitle: {
    fontSize: 10,
    fontWeight: 500,
    marginBottom: 3,
    fontFamily: 'Helvetica Neue, sans-serif',
    color: theme.palette.common.black,
  },
  cardText: {
    fontSize: 10,
    fontWeight: 400,
    fontFamily: 'Helvetica Neue, sans-serif',
    color: theme.palette.common.black,
  },
}));

const ModuleBlock: FC<ModuleBlockProps> = ({ title, listItems }) => {
  const classes = useStyles();
  const isMediumUp = useMediaUp('md');

  return (
    <Box className={classes.root}>
      <Grid container justifyContent="space-between" alignItems="center" spacing={isMediumUp ? 6 : 0}>
        <Grid xs={12} md={3} item>
          <Typography variant="h2" className={classes.title}>
            {title}
          </Typography>
          <Typography className={classes.description}>
            To learn more about Modules check out{' '}
            <a href="https://gpmgis.com/" target="_blank" rel="noreferrer">
              GPMGIS.COM
            </a>{' '}
            website
          </Typography>
          <Box mt={8} mb={5}>
            <a className={classes.link} href="https://gpmgis.com/" target="_blank" rel="noopener noreferrer">
              Visit website
            </a>
          </Box>
        </Grid>

        <Grid xs={12} md={9} item>
          <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" position="relative">
            {/* Top Center */}
            <Box mb={6}>
              <Paper className={classes.cardTop}>
                <Box textAlign="center">
                  {listItems[0].icon}
                  <Typography className={classes.cardTitle}>{listItems[0].title}</Typography>
                  <Typography className={classes.cardText}>{listItems[0].text}</Typography>
                </Box>
              </Paper>
            </Box>

            {/* Middle Row */}
            <Box display="flex" flexDirection="row" justifyContent="center" alignItems="flex-start">
              {/* Left */}
              <Box mr={isMediumUp ? 6 : 3}>
                <Paper className={classes.card}>
                  <Box textAlign="center">
                    {listItems[1].icon}
                    <Typography className={classes.cardTitle}>{listItems[1].title}</Typography>
                    <Typography className={classes.cardText}>{listItems[1].text}</Typography>
                  </Box>
                </Paper>
              </Box>

              {/* Right */}
              <Box ml={isMediumUp ? 6 : 3}>
                <Paper className={classes.card}>
                  <Box textAlign="center">
                    {listItems[2].icon}
                    <Typography className={classes.cardTitle}>{listItems[2].title}</Typography>
                    <Typography className={classes.cardText}>{listItems[2].text}</Typography>
                  </Box>
                </Paper>
              </Box>
            </Box>

            {/* Bottom Center */}
            <Box mt={6}>
              <Paper className={classes.cardTop}>
                <Box textAlign="center">
                  {listItems[3].icon}
                  <Typography className={classes.cardTitle}>{listItems[3].title}</Typography>
                  <Typography className={classes.cardText}>{listItems[3].text}</Typography>
                </Box>
              </Paper>
            </Box>
          </Box>
        </Grid>
      </Grid>
    </Box>
  );
};

export default ModuleBlock;
